import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { ArrowRight, Shield, User, Mail, Phone, FileText, AlertCircle } from 'lucide-react';
import { submitLead, getGeoLocation, buildLeadPayload, getCountryCodeFromDialCode } from '../services/api';
import { validateEmail, validatePhone, validateName, validateRequired } from '../utils/validation';
import { openZendeskChat } from '../utils/chat';

const HeroSection = ({ onOpenModal }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    phone: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, formData[field]);
  };

  const validateField = (field, value) => {
    let validation = { isValid: true, error: '' };

    switch (field) {
      case 'name':
        validation = validateName(value);
        break;
      case 'email':
        validation = validateEmail(value);
        break;
      case 'phone':
        validation = validatePhone(value, formData.countryCode);
        break;
      case 'description':
        validation = validateRequired(value, 'Description', 1);
        break;
      default:
        break;
    }

    setErrors(prev => ({
      ...prev,
      [field]: validation.error
    }));

    return validation.isValid;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Validate on change if field was already touched
    if (touched[field]) {
      validateField(field, value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mark all fields as touched
    setTouched({
      name: true,
      email: true,
      phone: true,
      description: true
    });

    // Validate all fields
    const nameValid = validateField('name', formData.name);
    const emailValid = validateField('email', formData.email);
    const phoneValid = validateField('phone', formData.phone);
    const descValid = validateField('description', formData.description);

    if (!nameValid || !emailValid || !phoneValid || !descValid) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the errors in the form before submitting',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Fetch geolocation data
      const geoData = await getGeoLocation();
      
      // Extract numeric dial code from countryCode
      const dialCode = formData.countryCode.replace('+', '');
      
      // Build complete payload
      const payload = buildLeadPayload({
        name: formData.name,
        email: formData.email,
        countryDialCode: dialCode,
        phoneNumber: formData.phone,
        description: formData.description
      }, geoData);

      // Submit lead
      const result = await submitLead(payload);

      if (result.success && result.statusCode === 200) {
        toast({
          title: 'Success!',
          description: result.message || 'Your request has been submitted successfully.'
        });
        
        // Reset form
        setFormData({ name: '', email: '', countryCode: '+1', phone: '', description: '' });
        setErrors({});
        setTouched({});
        
        // Redirect to application page with customerId
        if (result.customerId) {
          location.href=`/thanks?customerid=${result.customerId}`;
        }
      } else {
        toast({
          title: 'Error',
          description: result.message || 'Something went wrong. Please try again.',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden pt-20">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-green-50 via-yellow-50 to-white">
        <div className="absolute top-20 left-10 w-72 h-72 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-yellow-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-green-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-6 animate-fade-in">
            <div className="inline-flex items-center space-x-2 bg-green-100 px-4 py-2 rounded-full">
              <Shield className="text-brand-primary" size={20} />
              <span className="text-brand-primary font-medium">Trusted Brand Protection</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold leading-tight">
              Safeguard Your Brand,{' '}
              <span className="bg-brand-gradient bg-clip-text text-transparent">
                Secure Your Future
              </span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              At Trademark Law Associates, we specialize in safeguarding your intellectual property and ensuring the long-term security of your brand. Our team of experts processes your trademark application swiftly within 24 hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                className="bg-brand-gradient text-white hover:opacity-90 group shadow-lg"
                onClick={onOpenModal}
              >
                Get Started Now
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>
              <Button
                size="lg"
                className="bg-white border-2 border-brand-primary text-brand-primary hover:bg-gray-50 shadow-lg"
                onClick={openZendeskChat}
              >
                Live Chat
              </Button>
            </div>
          </div>

          {/* Right Form */}
          <div id="registration-form" className="bg-white rounded-2xl shadow-2xl p-6 animate-slide-up border border-emerald-100">
            <div className="text-center mb-5">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-brand-gradient rounded-full mb-3">
                <Shield className="text-white" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-1">
                Secure Your Brand Identity for Only <span className="text-brand-primary">$45</span>
              </h3>
              <p className="text-sm text-gray-600">Protect your brand with trusted expertise</p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-3.5">
              {/* Name Field */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-700 flex items-center">
                  <User className="mr-1.5 text-brand-primary" size={14} />
                  Name <span className="text-red-500 ml-0.5">*</span>
                </label>
                <Input
                  type="text"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  onBlur={() => handleBlur('name')}
                  className={`h-10 text-sm transition-all ${
                    errors.name && touched.name
                      ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200'
                      : 'border-gray-300 focus:border-brand-primary focus:ring-2 focus:ring-green-200'
                  }`}
                />
                {errors.name && touched.name && (
                  <div className="flex items-center text-xs text-red-600 mt-1">
                    <AlertCircle size={12} className="mr-1" />
                    {errors.name}
                  </div>
                )}
              </div>

              {/* Email Field */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-700 flex items-center">
                  <Mail className="mr-1.5 text-brand-primary" size={14} />
                  Email <span className="text-red-500 ml-0.5">*</span>
                </label>
                <Input
                  type="email"
                  placeholder="Enter your email address"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  onBlur={() => handleBlur('email')}
                  className={`h-10 text-sm transition-all ${
                    errors.email && touched.email
                      ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200'
                      : 'border-gray-300 focus:border-brand-primary focus:ring-2 focus:ring-green-200'
                  }`}
                />
                {errors.email && touched.email && (
                  <div className="flex items-center text-xs text-red-600 mt-1">
                    <AlertCircle size={12} className="mr-1" />
                    {errors.email}
                  </div>
                )}
              </div>

              {/* Contact Number with Country Code */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-700 flex items-center">
                  <Phone className="mr-1.5 text-brand-primary" size={14} />
                  Contact Number <span className="text-red-500 ml-0.5">*</span>
                </label>
                <div className="flex gap-2">
                  <Select 
                    value={formData.countryCode} 
                    onValueChange={(value) => handleChange('countryCode', value)}
                  >
                    <SelectTrigger className="w-24 h-10 text-sm border-gray-300 focus:border-brand-primary focus:ring-2 focus:ring-green-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="+1">🇺🇸 +1</SelectItem>
                      <SelectItem value="+44">🇬🇧 +44</SelectItem>
                      <SelectItem value="+91">🇮🇳 +91</SelectItem>
                      <SelectItem value="+61">🇦🇺 +61</SelectItem>
                      <SelectItem value="+81">🇯🇵 +81</SelectItem>
                      <SelectItem value="+86">🇨🇳 +86</SelectItem>
                      <SelectItem value="+49">🇩🇪 +49</SelectItem>
                      <SelectItem value="+33">🇫🇷 +33</SelectItem>
                      <SelectItem value="+39">🇮🇹 +39</SelectItem>
                      <SelectItem value="+7">🇷🇺 +7</SelectItem>
                      <SelectItem value="+55">🇧🇷 +55</SelectItem>
                      <SelectItem value="+82">🇰🇷 +82</SelectItem>
                      <SelectItem value="+34">🇪🇸 +34</SelectItem>
                      <SelectItem value="+31">🇳🇱 +31</SelectItem>
                      <SelectItem value="+46">🇸🇪 +46</SelectItem>
                      <SelectItem value="+41">🇨🇭 +41</SelectItem>
                      <SelectItem value="+65">🇸🇬 +65</SelectItem>
                      <SelectItem value="+60">🇲🇾 +60</SelectItem>
                      <SelectItem value="+63">🇵🇭 +63</SelectItem>
                      <SelectItem value="+971">🇦🇪 +971</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="tel"
                    placeholder="Enter phone number"
                    value={formData.phone}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      handleChange('phone', value);
                    }}
                    onBlur={() => handleBlur('phone')}
                    maxLength={10}
                    className={`flex-1 h-10 text-sm transition-all ${
                      errors.phone && touched.phone
                        ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200'
                        : 'border-gray-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200'
                    }`}
                  />
                </div>
                {errors.phone && touched.phone && (
                  <div className="flex items-center text-xs text-red-600 mt-1">
                    <AlertCircle size={12} className="mr-1" />
                    {errors.phone}
                  </div>
                )}
              </div>

              {/* Description Field */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-700 flex items-center">
                  <FileText className="mr-1.5 text-brand-primary" size={14} />
                  Description <span className="text-red-500 ml-0.5">*</span>
                </label>
                <Textarea
                  placeholder="Tell us about your trademark needs..."
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  onBlur={() => handleBlur('description')}
                  className={`min-h-[70px] text-sm transition-all resize-none ${
                    errors.description && touched.description
                      ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200'
                      : 'border-gray-300 focus:border-brand-primary focus:ring-2 focus:ring-green-200'
                  }`}
                />
                {errors.description && touched.description && (
                  <div className="flex items-center text-xs text-red-600 mt-1">
                    <AlertCircle size={12} className="mr-1" />
                    {errors.description}
                  </div>
                )}
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-brand-gradient text-white hover:opacity-90 h-11 text-base font-semibold shadow-lg hover:shadow-xl transition-all group mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Start Your Registration'}
                {!isSubmitting && <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />}
              </Button>
              
              <p className="text-xs text-gray-500 text-center leading-relaxed pt-1">
                By submitting, you agree to our <span className="text-brand-primary hover:underline cursor-pointer">Terms of Service</span> and <span className="text-brand-primary hover:underline cursor-pointer">Privacy Policy</span>
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
